import React, { useState, useEffect, useRef } from "react";

const API_URL = "/php/mini_ulohy_api.php";

async function loadFromServer(pin) {
  try {
    const res = await fetch(API_URL + "?action=load&pin=" + encodeURIComponent(pin));
    if (!res.ok) throw new Error("load failed");
    return await res.json();
  } catch {
    return null;
  }
}

async function saveToServer(tasks, pin) {
  try {
    await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "save", tasks, pin }),
    });
  } catch {
    // tichý fail
  }
}

export default function MiniUlohy({ collapsed, onToggle, userPin }) {
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [inputText, setInputText] = useState("");
  const [inputUrgent, setInputUrgent] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const inputRef = useRef(null);
  const saveTimer = useRef(null);

  // Načítanie zo servera pri štarte (len keď je PIN k dispozícii)
  useEffect(() => {
    if (!userPin) return;
    setLoaded(false);
    loadFromServer(userPin).then((serverTasks) => {
      if (Array.isArray(serverTasks) && serverTasks.length > 0) {
        setTasks(serverTasks);
        setLoaded(true);
      } else {
        // fallback na localStorage (migrácia zo starej verzie)
        try {
          const raw = localStorage.getItem("miniUlohy_v1");
          const local = raw ? JSON.parse(raw) : [];
          if (local.length > 0) {
            setTasks(local);
            saveToServer(local, userPin);
            localStorage.removeItem("miniUlohy_v1");
          }
        } catch { /* ignoruj */ }
        setLoaded(true);
      }
    });
  }, [userPin]);

  // Ukladanie na server hneď po každej zmene úloh (nie pri počiatočnom načítaní)
  useEffect(() => {
    if (!loaded || !userPin) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveToServer(tasks, userPin), 500);
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKey = (e) => {
      if (e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        if (collapsed) onToggle();
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [collapsed, onToggle]);

  const addTask = (e) => {
    e.preventDefault();
    const text = inputText.trim();
    if (!text) return;
    const newTask = {
      id: Date.now().toString() + Math.random().toString(36).slice(2),
      text,
      deadline: null,
      urgent: inputUrgent,
      done: false,
      createdAt: new Date().toISOString(),
    };
    setTasks((prev) => [newTask, ...prev]);
    setInputText("");
    setInputUrgent(false);
    inputRef.current?.focus();
  };

  const toggleDone = (id) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );
  };

  const deleteTask = (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const toggleUrgent = (id) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, urgent: !t.urgent } : t))
    );
  };

  const deleteDone = () => {
    setTasks((prev) => prev.filter((t) => !t.done));
  };

  const pending = tasks.filter((t) => !t.done).sort((a, b) => {
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
    if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
    if (a.deadline) return -1;
    if (b.deadline) return 1;
    return 0;
  });
  const done = tasks.filter((t) => t.done);

  const panelStyle = {
    width: collapsed ? 38 : 340,
    minWidth: collapsed ? 38 : 340,
    maxWidth: collapsed ? 38 : 340,
    background: "#f5f0e8",
    border: "1px solid #d4c9b8",
    borderRadius: 14,
    padding: collapsed ? "10px 0" : "14px 14px 12px 14px",
    alignSelf: "flex-start",
    boxShadow: "0 2px 12px rgba(90,50,130,0.10)",
    flexShrink: 0,
  };

  if (collapsed) {
    return (
      <div style={panelStyle}>
        <button
          onClick={onToggle}
          title="Rozbaliť miniúlohy"
          style={{ display: "block", width: "100%", background: "none", border: "none",
            cursor: "pointer", fontSize: 16, padding: "4px 0", textAlign: "center" }}
        >
          ✏️
        </button>
        {pending.length > 0 && (
          <div style={{ textAlign: "center", fontSize: 11,
            color: "#fff", background: pending.some((t) => t.urgent) ? "#b91c1c" : "#7c3aed",
            borderRadius: 10, padding: "2px 5px", fontWeight: 700, marginTop: 2 }}
          >
            {pending.length}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      {/* Hlavička */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15 }}>✏️</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#1e1530" }}>Miniúlohy</span>
          {pending.length > 0 && (
            <span style={{
              background: pending.some((t) => t.urgent) ? "#b91c1c" : "#7c3aed",
              color: "#fff", borderRadius: 12, fontSize: 12,
              fontWeight: 700, padding: "2px 9px",
            }}>{pending.length}</span>
          )}
        </div>
        <button
          onClick={onToggle}
          title="Zbaliť"
          style={{ background: "none", border: "none", color: "#7c6e8a",
            cursor: "pointer", fontSize: 18, padding: "0 2px", lineHeight: 1 }}
        >
          ‹
        </button>
      </div>

      {/* Formulár */}
      <form onSubmit={addTask} style={{ marginBottom: 12 }}>
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Nová úloha…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "7px 11px",
            borderRadius: 8,
            border: "1.5px solid #c4b8d4",
            background: "#fff",
            color: "#1e1530",
            fontSize: 14,
            marginBottom: 7,
            outline: "none",
          }}
          onFocus={e => e.target.style.borderColor = "#7c3aed"}
          onBlur={e => e.target.style.borderColor = "#c4b8d4"}
        />
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setInputUrgent((v) => !v)}
            style={{
              padding: "6px 12px",
              borderRadius: 7,
              border: `1.5px solid ${inputUrgent ? "#b91c1c" : "#c4b8d4"}`,
              background: inputUrgent ? "#fef2f2" : "#fff",
              color: inputUrgent ? "#b91c1c" : "#7c6e8a",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            ! Urgentné
          </button>
          <button
            type="submit"
            disabled={!inputText.trim()}
            style={{
              padding: "6px 16px",
              borderRadius: 7,
              border: "none",
              background: inputText.trim() ? "#7c3aed" : "#e0d8f0",
              color: inputText.trim() ? "#fff" : "#7c6e8a",
              cursor: inputText.trim() ? "pointer" : "default",
              fontSize: 14,
              fontWeight: 700,
              marginLeft: "auto",
            }}
          >
            + Pridať
          </button>
        </div>
      </form>

      {(pending.length > 0 || done.length > 0) && (
        <div style={{ height: 1, background: "#d4c9b8", marginBottom: 10 }} />
      )}

      {/* Zoznam čakajúcich úloh */}
      <div style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
        {pending.length === 0 && done.length === 0 && (
          <div style={{ color: "#7c6e8a", fontSize: 13, textAlign: "center", padding: "10px 0" }}>
            Žiadne úlohy
          </div>
        )}
        {pending.map((task) => (
          <div
            key={task.id}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
              padding: "7px 8px",
              borderRadius: 8,
              marginBottom: 5,
              background: task.urgent ? "#fef2f2" : "#fff",
              border: `1px solid ${task.urgent ? "#fca5a5" : "#e8e0f0"}`,
            }}
          >
            {/* Checkbox */}
            <button
              onClick={() => toggleDone(task.id)}
              title="Označiť ako hotové"
              style={{
                flexShrink: 0, marginTop: 2,
                width: 18, height: 18, borderRadius: 5,
                border: `2px solid ${task.urgent ? "#fca5a5" : "#c4b8d4"}`,
                background: "#fff", cursor: "pointer", padding: 0,
              }}
            />
            {/* Text */}
            <span style={{
              flex: 1, fontSize: 14,
              color: task.urgent ? "#b91c1c" : "#1e1530",
              wordBreak: "break-word", lineHeight: 1.4, paddingTop: 1,
              fontWeight: task.urgent ? 600 : 400,
            }}>
              {task.urgent && <span style={{ marginRight: 4 }}>⚡</span>}
              {task.text}
            </span>
            {/* Akcie */}
            <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, paddingTop: 1 }}>
              <button onClick={() => toggleUrgent(task.id)}
                title={task.urgent ? "Zrušiť urgentné" : "Označiť urgentné"}
                style={{ background: "none", border: "none", cursor: "pointer",
                  color: task.urgent ? "#b91c1c" : "#c4b8d4",
                  fontSize: 14, fontWeight: 700, padding: "1px 4px", lineHeight: 1 }}>!</button>
              <button onClick={() => deleteTask(task.id)} title="Zmazať"
                style={{ background: "none", border: "none", cursor: "pointer",
                  color: "#c4b8d4", fontSize: 16, padding: "0 4px", lineHeight: 1 }}>×</button>
            </div>
          </div>
        ))}
      </div>

      {/* Hotové úlohy */}
      {done.length > 0 && (
        <div style={{ marginTop: 10, borderTop: "1px solid #d4c9b8", paddingTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
            <button onClick={() => setShowDone((v) => !v)} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#7c6e8a", fontSize: 13, padding: 0, fontWeight: 500,
            }}>
              {showDone ? "▾" : "▸"} Hotové ({done.length})
            </button>
            <button onClick={deleteDone} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#7c6e8a", fontSize: 12, padding: 0,
            }}>Vyčistiť</button>
          </div>
          {showDone && done.map((task) => (
            <div key={task.id} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "5px 8px", marginBottom: 3, borderRadius: 7,
              background: "#ede9fe",
            }}>
              <button onClick={() => toggleDone(task.id)} title="Vrátiť do zoznamu" style={{
                flexShrink: 0, width: 18, height: 18, borderRadius: 5,
                border: "2px solid #7c3aed", background: "#ede9fe",
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", padding: 0,
                color: "#7c3aed", fontSize: 11, fontWeight: 700,
              }}>✓</button>
              <span style={{
                fontSize: 13, color: "#a89cbf",
                textDecoration: "line-through", flex: 1, wordBreak: "break-word",
              }}>{task.text}</span>
              <button onClick={() => deleteTask(task.id)} style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#c4b8d4", fontSize: 15, padding: "0 2px",
              }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
